import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/auth";
import CharacterPicker from "@/components/CharacterPicker";
import DailyCypherCard from "@/components/DailyCypherCard";
import UserAvatar from "@/components/UserAvatar";
import Achievements from "@/components/Achievements";

export function Leaderboard() {
 const [rows, setRows] = useState([]);
 useEffect(() => { axios.get(`${API}/leaderboard`).then(r => setRows(r.data)); }, []);

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-yellow mb-2"> RANKINGS</div>
 <h1 className="font-display text-6xl md:text-7xl tracking-tight mb-10">LEADERBOARD</h1>

 {rows.length === 0 ? (
 <div className="bg-yt-surface border border-yt-border p-12 text-center">
 <div className="font-display text-3xl tracking-tight text-yt-muted">NO BATTLES YET</div>
 <div className="text-sm text-yt-muted mt-2">Be the first to step into the cypher.</div>
 <Link to="/register" data-testid="leaderboard-register-cta" className="inline-block mt-6 bg-yt-yellow text-black font-display text-lg tracking-wider px-6 py-3 shadow-brutal">ENTER ▶</Link>
 </div>
 ) : (
 <div className="border border-yt-border bg-yt-surface overflow-x-auto">
 <table className="w-full font-mono text-sm">
 <thead className="bg-black border-b border-yt-border text-yt-muted">
 <tr>
 <th className="text-left p-4 w-12">#</th>
 <th className="text-left p-4">RAPPER</th>
 <th className="text-right p-4">W</th>
 <th className="text-right p-4">L</th>
 <th className="text-right p-4">WR</th>
 <th className="text-right p-4">STREAK</th>
 <th className="text-right p-4">PTS</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => (
 <tr key={r.rapper_name} data-testid={`leaderboard-row-${i}`} className={`border-b border-yt-border ${i === 0 ? "bg-yt-yellow/5" : ""}`}>
 <td className="p-4 font-display text-xl text-yt-yellow">#{i+1}</td>
 <td className="p-4">
 <div className="flex items-center gap-3">
 <UserAvatar portrait={r.portrait} name={r.rapper_name} size="sm" />
 <div>
 <div className="font-display text-lg tracking-tight">{r.rapper_name}</div>
 <div className="text-[10px] text-yt-muted">{r.style?.toUpperCase()}</div>
 </div>
 </div>
 </td>
 <td className="p-4 text-right text-yt-judge">{r.wins}</td>
 <td className="p-4 text-right text-yt-red">{r.losses}</td>
 <td className="p-4 text-right">{r.win_rate}%</td>
 <td className="p-4 text-right">{r.streak > 0 ? ` ${r.streak}` : "—"}</td>
 <td className="p-4 text-right font-display text-yt-yellow">{r.monthly_points}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 );
}

export function Tournament() {
 const [rows, setRows] = useState([]);
 useEffect(() => { axios.get(`${API}/leaderboard`).then(r => setRows(r.data.slice(0, 12))); }, []);
 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> MONTHLY BRACKET</div>
 <h1 className="font-display text-6xl md:text-7xl tracking-tight mb-3">TOURNAMENT</h1>
 <p className="text-yt-muted max-w-2xl mb-10">Top 12 rappers by monthly points battle for the crown — Champion gets real Young Tongues swag, endorsement, and homepage feature.</p>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {rows.length === 0 && <div className="col-span-3 bg-yt-surface border border-yt-border p-12 text-center font-mono text-yt-muted">Bracket builds when battles happen. Earn points to qualify.</div>}
 {rows.map((r, i) => (
 <div key={r.rapper_name} className="bg-yt-surface border border-yt-border p-5 flex items-center gap-4">
 <div className="font-display text-4xl text-yt-yellow">#{i+1}</div>
 <UserAvatar portrait={r.portrait} name={r.rapper_name} size="lg" />
 <div className="flex-1 min-w-0">
 <div className="font-display text-xl tracking-tight truncate">{r.rapper_name}</div>
 <div className="text-[10px] font-mono text-yt-muted">{r.monthly_points} PTS</div>
 </div>
 {i < 12 && <div className="font-mono text-[10px] text-yt-judge tracking-widest">QUALIFIED</div>}
 </div>
 ))}
 </div>

 <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-4">
 {[
 ["WIN", "+10 PTS"],
 ["LOSS", "+2 PTS"],
 ["JUDGE 90+", "+5 PTS"],
 ["BATTLE", "+2 PTS"],
 ].map(([k, v]) => (
 <div key={k} className="bg-yt-surface border border-yt-border p-5 text-center">
 <div className="font-display text-3xl text-yt-yellow">{v}</div>
 <div className="font-mono text-[10px] text-yt-muted tracking-widest mt-1">{k}</div>
 </div>
 ))}
 </div>
 </div>
 );
}

export function MyProfile() {
 const { user, refresh } = useAuth();
 const [battles, setBattles] = useState([]);
 const [challenges, setChallenges] = useState({ incoming: [], outgoing: [] });
 const [editing, setEditing] = useState(false);
 const [saving, setSaving] = useState(false);
 const [editPortrait, setEditPortrait] = useState(user?.portrait || null);
 useEffect(() => {
 if (user) {
   axios.get(`${API}/battles/mine`).then(r => setBattles(r.data));
   axios.get(`${API}/challenges/mine`).then(r => setChallenges(r.data)).catch(() => {});
 }
 setEditPortrait(user?.portrait || null);
 }, [user]);

 const savePortrait = async () => {
 setSaving(true);
 try {
 await axios.patch(`${API}/users/me`, { portrait: editPortrait });
 await refresh();
 setEditing(false);
 } catch {}
 finally { setSaving(false); }
 };

 if (!user) return <div className="p-10 text-center"><Link to="/login" className="text-yt-yellow underline">Sign in</Link></div>;
 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
 <DailyCypherCard />
 <Achievements />
 <div className="bg-yt-surface border border-yt-border p-8 mb-8">
 <div className="flex items-center gap-6 flex-wrap">
 {user.portrait ? (
 <div className="w-28 h-28 overflow-hidden border-2 border-yt-yellow bg-black">
 <img src={user.portrait} alt={user.rapper_name} className="w-full h-full object-cover" />
 </div>
 ) : <UserAvatar portrait={null} name={user.rapper_name} size="xl" className="!border-yt-yellow" />}
 <div className="flex-1">
 <div className="font-mono text-[10px] tracking-[0.3em] text-yt-muted">{user.style?.toUpperCase()}</div>
 <div className="font-display text-5xl tracking-tight">{user.rapper_name}</div>
 <div className="font-mono text-xs text-yt-yellow mt-1">TIER · {user.tier?.toUpperCase()} · {user.battles_remaining} BATTLES LEFT</div>
 </div>
 <div className="flex flex-col gap-2">
 <button data-testid="profile-edit-character" onClick={() => setEditing(e => !e)} className="border border-yt-border text-white font-mono text-xs tracking-widest px-4 py-2 hover:border-yt-yellow hover:text-yt-yellow">
 {editing ? "CLOSE" : "✎ CHARACTER"}
 </button>
 <Link to="/battles/new" data-testid="profile-new-battle" className="bg-yt-yellow text-black font-display text-lg tracking-wider px-6 py-3 shadow-brutal text-center">▶ NEW BATTLE</Link>
 </div>
 </div>
 {editing && (
 <div className="mt-6 border-t border-yt-border pt-6">
 <CharacterPicker value={editPortrait} onChange={setEditPortrait} testIdPrefix="profile-char" />
 <div className="flex gap-2 mt-4">
 <button data-testid="save-portrait-btn" onClick={savePortrait} disabled={saving}
 className="bg-yt-yellow text-black font-display text-base tracking-wider px-5 py-2 shadow-brutal disabled:opacity-50">
 {saving ? "SAVING..." : "SAVE CHARACTER"}
 </button>
 <button onClick={() => { setEditPortrait(user.portrait || null); setEditing(false); }} className="border border-yt-border px-5 py-2 font-mono text-xs tracking-widest hover:border-yt-red">CANCEL</button>
 </div>
 </div>
 )}
 <div className="grid grid-cols-4 gap-4 mt-8">
 {[["WINS", user.wins, "text-yt-judge"], ["LOSSES", user.losses, "text-yt-red"], ["STREAK", user.streak, "text-yt-yellow"], ["PTS", user.monthly_points, "text-yt-yellow"]].map(([k, v, c]) => (
 <div key={k} className="border border-yt-border bg-black p-4 text-center">
 <div className={`font-display text-4xl ${c}`}>{v}</div>
 <div className="font-mono text-[10px] text-yt-muted tracking-widest">{k}</div>
 </div>
 ))}
 </div>
 </div>

 <MyChallengesCard incoming={challenges.incoming} outgoing={challenges.outgoing} me={user.rapper_name} />

 <div className="font-display text-3xl tracking-tight mb-4">MY BATTLES</div>
 <div className="space-y-3">
 {battles.length === 0 && <div className="bg-yt-surface border border-yt-border p-8 text-center font-mono text-yt-muted">No battles yet. Step in the arena.</div>}
 {battles.map(b => (
 <Link to={`/battles/${b.id}`} key={b.id} data-testid={`profile-battle-${b.id}`} className="block bg-yt-surface border border-yt-border p-5 hover:border-yt-yellow transition-colors">
 <div className="flex items-center gap-4">
 <UserAvatar portrait={b.opponent_portrait} name={b.opponent_name} size="lg" />
 <div className="flex-1">
 <div className="font-display text-xl tracking-tight">vs {b.opponent_name}</div>
 <div className="font-mono text-[10px] text-yt-muted">{b.rounds_total} ROUNDS · {b.status?.toUpperCase()}</div>
 </div>
 <div className="font-display text-2xl">{b.score_user}<span className="text-yt-muted">:{b.score_opponent}</span></div>
 {b.status === "completed" && (
 <div className={`font-mono text-xs tracking-widest ${b.winner === "user" ? "text-yt-judge" : b.winner === "draw" ? "text-yt-yellow" : "text-yt-red"}`}>
 {b.winner === "user" ? "WON" : b.winner === "draw" ? "DRAW" : "LOST"}
 </div>
 )}
 </div>
 </Link>
 ))}
 </div>
 </div>
 );
}


function MyChallengesCard({ incoming, outgoing, me }) {
  const pendingIn = incoming.filter(c => c.status === "pending" || c.status === "scheduled");
  const pendingOut = outgoing.filter(c => c.status === "pending" || c.status === "scheduled");
  const totalActive = pendingIn.length + pendingOut.length;
  const recent = [...incoming, ...outgoing]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 3);

  const fmtCountdown = (iso) => {
    if (!iso) return null;
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return "NOW";
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <div data-testid="me-challenges-card" className="bg-yt-surface border-2 border-yt-yellow p-6 mb-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-yt-red mb-1">● YOUR CHALLENGES</div>
          <div className="font-display text-3xl tracking-tight">MY CHALLENGES</div>
        </div>
        <Link to="/inbox" data-testid="me-open-inbox" className="bg-yt-yellow text-black font-display text-base tracking-wider px-5 py-2 shadow-brutal hover:bg-white transition-colors">
          OPEN INBOX ▶
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="border border-yt-border bg-black p-4 text-center">
          <div className="font-display text-4xl text-yt-red">{pendingIn.length}</div>
          <div className="font-mono text-[10px] text-yt-muted tracking-widest">INCOMING · WAITING ON YOU</div>
        </div>
        <div className="border border-yt-border bg-black p-4 text-center">
          <div className="font-display text-4xl text-yt-yellow">{pendingOut.length}</div>
          <div className="font-mono text-[10px] text-yt-muted tracking-widest">OUTGOING · WAITING ON THEM</div>
        </div>
      </div>

      {totalActive === 0 && recent.length === 0 ? (
        <div className="text-center py-6">
          <div className="font-mono text-sm text-yt-muted mb-4">No challenges yet. Step to somebody.</div>
          <Link to="/rappers" className="inline-block bg-yt-red text-white font-display text-base tracking-wider px-5 py-2 shadow-brutal">▶ FIND A RAPPER</Link>
        </div>
      ) : (
        <div className="space-y-2">
          {recent.map(c => {
            const iAmChallenger = c.challenger_name === me;
            const other = iAmChallenger ? c.challenged_name : c.challenger_name;
            const label = c.status === "scheduled" && c.scheduled_at
              ? `SCHEDULED · IN ${fmtCountdown(c.scheduled_at)}`
              : c.status?.toUpperCase();
            return (
              <Link key={c.id} to={`/pvp/${c.id}`} data-testid={`me-challenge-${c.id}`}
                className="flex items-center justify-between bg-black border border-yt-border hover:border-yt-yellow p-3 transition-colors">
                <div>
                  <div className="font-mono text-[10px] text-yt-muted">{iAmChallenger ? "YOU CHALLENGED" : "CHALLENGED BY"}</div>
                  <div className="font-display text-lg text-white">{other}</div>
                </div>
                <div className="font-mono text-xs text-yt-yellow tracking-widest">{label}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
